/**
 * Configuration module for search tracking functionality.
 * Provides default configurations and methods to create customized tracking configurations.
 * @module tracking-config
 */

import type {
  SearchEventTrackingConfig,
  SearchProviderEnable,
  SearchTrackingConfig,
  SearchContainer,
  SearchConfig
} from "../types/tracking-config";
import { DEFAULTS } from "../search-ui";

/**
 * Default tracking configuration for different search providers
 * Includes event names and parameter keys for tracking
 */
const DEFAULT_TRACKING: Required<SearchEventTrackingConfig> = {
  BLOOMREACH: {
    events: {
      searchTracking: "br_search_tracking",
      searchFormSubmitted: "br_searchpage_hit",
      allResultsClicked: "br_search_all_results_click",
    },
    parameters: {
      searchTerm: "br_search_term"
    }
  },
  SALESFIRE: {
    events: {
      searchTracking: "sf_search_tracking",
      searchFormSubmitted: "sf_searchpage_hit",
      allResultsClicked: "sf_search_all_results_click",
    },
    parameters: {
      searchTerm: "sf_search_term"
    }
  }
} as const;

/**
 * Default options for enabling/disabling different search providers
 */
const DEFAULT_OPTIONS: Required<SearchProviderEnable> = {
  enableBloomreach: false,
  enableSalesfire: false,
} as const;

/**
 * Default container configurations for different search providers
 * Includes selectors for containers, inputs, and iframes
 */
const DEFAULT_CONTAINERS: SearchContainer[] = [
  {
    providerId: 'BLOOMREACH',
    selectors: {
      container: DEFAULTS.searchContainer,
      searchInput: DEFAULTS.searchInput
    }
  },
  {
    providerId: 'SALESFIRE',
    selectors: {
      container: '#salesfire-search',
      searchInput: 'input[type="search"]',
      iframeSelector: 'iframe'
    }
  }
];

/**
 * Default search configuration including timing parameters
 */
const DEFAULT_SEARCH_CONFIG: Required<SearchConfig> = {
  typingInterval: 400,
  initDelay: 200
} as const;

/**
 * Creates tracking types configuration by merging defaults with custom config
 * @param config - Optional custom tracking configuration
 * @returns Merged tracking types configuration
 */
function createTrackingTypes(config?: SearchTrackingConfig) {
  if (!config?.trackingTypes) return DEFAULT_TRACKING;

  return {
    BLOOMREACH: {
      ...DEFAULT_TRACKING.BLOOMREACH,
      ...(config.trackingTypes.BLOOMREACH || {})
    },
    SALESFIRE: {
      ...DEFAULT_TRACKING.SALESFIRE,
      ...(config.trackingTypes.SALESFIRE || {})
    }
  } as const;
}

/**
 * Creates tracking options configuration by merging defaults with custom options
 * @param options - Optional custom provider enable options
 * @returns Merged tracking options configuration
 */
function createTrackingOptions(options?: SearchProviderEnable): Required<SearchProviderEnable> {
  if (!options) return DEFAULT_OPTIONS;

  return {
    ...DEFAULT_OPTIONS,
    ...(options.enableBloomreach !== undefined && { enableBloomreach: options.enableBloomreach }),
    ...(options.enableSalesfire !== undefined && { enableSalesfire: options.enableSalesfire })
  };
}

/**
 * Creates container configuration by merging defaults with custom containers
 * @param config - Optional custom tracking configuration
 * @returns Array of container configurations
 */
function createTrackingContainers(config?: SearchTrackingConfig): SearchContainer[] {
  if (!config?.containers) return [...DEFAULT_CONTAINERS]; // Create a new array instance

  // Return user containers if provided, otherwise use defaults
  return config.containers.length > 0 ? [...config.containers] : [...DEFAULT_CONTAINERS];
}

/**
 * Creates search configuration by merging defaults with custom config
 * @param config - Optional custom tracking configuration
 * @returns Merged search configuration
 */
function createSearchConfig(config?: SearchTrackingConfig) {
  if (!config) return DEFAULT_SEARCH_CONFIG;

  return {
    ...DEFAULT_SEARCH_CONFIG,
    ...(config.typingInterval !== undefined && { typingInterval: config.typingInterval }),
    ...(config.initDelay !== undefined && { initDelay: config.initDelay })
  };
}

/**
 * Complete tracking configuration interface
 * Combines all configuration aspects into a single type
 */
export interface TrackingConfiguration {
  trackingTypes: ReturnType<typeof createTrackingTypes>;
  trackingOptions: ReturnType<typeof createTrackingOptions>;
  searchConfig: ReturnType<typeof createSearchConfig>;
  containers: ReturnType<typeof createTrackingContainers>;
}

/**
 * Creates a complete tracking configuration by combining all configuration aspects
 * @param config - Optional custom tracking configuration
 * @returns Complete tracking configuration object
 */
export function createTrackingConfig(config?: SearchTrackingConfig): TrackingConfiguration {
  return {
    trackingTypes: createTrackingTypes(config),
    trackingOptions: createTrackingOptions(config?.options),
    searchConfig: createSearchConfig(config),
    containers: createTrackingContainers(config)
  };
}

export {
  createTrackingTypes,
  createTrackingOptions,
  createSearchConfig,
  createTrackingContainers,
  DEFAULT_TRACKING,
  DEFAULT_OPTIONS,
  DEFAULT_CONTAINERS,
  DEFAULT_SEARCH_CONFIG
};

export default createTrackingConfig;
